import { GenericTable } from "@shared/components/GenericTable";
import { Badge } from "@shared/ui/badge";
import { Tabs } from "@shared/ui/tabs";
import { Lock } from "lucide-react";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckDetailModal } from "../components/CheckDetailModal";
import { CheckRegisterFilter } from "./filter";
import {
  useCurrency,
  useRemoteUtilityId,
} from "@shared/selectors/globalSelectors";
import { useCheques } from "@features/cx/paymentsv2/hooks";
import { TableAction, TableColumn } from "@shared/types/table";
import { paymentsApi } from "@features/cx/paymentsv2/api";

const TableContent = ({
  statusTab,
  currentSearch,
  currentBank,
  currentSource,
  currentPage,
  columns,
  actions,
  filterPanel,
  handleSearch,
  handlePageChange,
}: any) => {
  const remoteUtilityId = useRemoteUtilityId();

  const { data: recordsResponse, isLoading } = useCheques({
    remoteUtilityId: Number(remoteUtilityId),
    tab: statusTab === "" ? "all" : statusTab,
    search: currentSearch,
    bank: currentBank,
    source: currentSource,
    page: currentPage,
    limit: 10,
  });

  const mappedRecords = useMemo(() => {
    if (!recordsResponse?.results) return [];
    return recordsResponse.results.map((r: any) => ({
      id: r.id,
      checkNo: r.chequeNumber || "N/A",
      checkRef: r.checkRef || r.check_ref || "N/A",
      consumerName: r.consumerName || r.consumer_name || "N/A",
      consumerAcc: r.consumerNo || "N/A",
      bankName: r.bank || "N/A",
      branchName: r.branch || "N/A",
      amount: r.amount || "N/A",
      penalty: r.penalty || "—",
      checkDate: r.chequeDate || "N/A",
      depositBy: r.depositBy || "N/A",
      depositStatus: r.depositStatus || "N/A",
      source: r.channel || "N/A",
      status: r.statusDisplay || "N/A",
      hold: r.holdActive ? "Active" : "Inactive",
    }));
  }, [recordsResponse?.results]);

  const totalItems = recordsResponse?.count || 0;

  const paginationData = {
    currentPage,
    totalPages: Math.ceil(totalItems / 10),
    totalItems: totalItems,
    itemsPerPage: 10,
    startItem: (currentPage - 1) * 10 + 1,
    endItem: Math.min(currentPage * 10, totalItems),
  };

  const handleExport = async () => {
    try {
      const blob = await paymentsApi.exportCheques({
        remoteUtilityId: Number(remoteUtilityId),
        tab: statusTab === "" ? "all" : statusTab,
        search: currentSearch,
        bank: currentBank,
        source: currentSource,
      });
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `cheques_${statusTab === "" ? "all" : statusTab}_${new Date().getTime()}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  return (
    <div className="mt-4 bg-white">
      <GenericTable
        data={mappedRecords}
        columns={columns as any}
        actions={actions}
        rowKey="id"
        loading={isLoading}
        searchPlaceholder="Search check #, consumer, bank, amount..."
        searchParamName="search"
        onSearch={handleSearch}
        pagination={paginationData}
        onPageChange={handlePageChange}
        filterPanel={filterPanel}
        onExport={handleExport}
      />
    </div>
  );
};

export const CheckRegisterList = () => {
  const currency = useCurrency();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);

  const currentSearch = decodeURIComponent(searchParams.get("search") || "");
  const currentBank = searchParams.get("bank") || "";
  const currentSource = searchParams.get("source") || "";

  const currentFilters = useMemo(
    () => ({
      search: currentSearch,
      bank: currentBank,
      source: currentSource,
    }),
    [currentSearch, currentBank, currentSource],
  );

  const handleSearch = (searchTerm: string) => {
    const newParams: any = { ...Object.fromEntries(searchParams.entries()) };
    if (searchTerm.trim()) {
      newParams.search = encodeURIComponent(searchTerm.trim());
    } else {
      delete newParams.search;
    }
    newParams.page = "1";
    setSearchParams(newParams);
    setCurrentPage(1);
  };

  const handleApplyFilters = (newFilters: any) => {
    const newParams: any = { ...Object.fromEntries(searchParams.entries()) };
    if (newFilters.bank) newParams.bank = newFilters.bank;
    else delete newParams.bank;

    if (newFilters.source) newParams.source = newFilters.source;
    else delete newParams.source;

    newParams.page = "1";
    setSearchParams(newParams);
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    const newParams: any = { ...Object.fromEntries(searchParams.entries()) };
    delete newParams.bank;
    delete newParams.source;
    delete newParams.search;
    newParams.page = "1";
    setSearchParams(newParams);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      page: String(page),
    };
    setSearchParams(newParams);
    setCurrentPage(page);
  };

  const columns: TableColumn<any>[] = [
    {
      key: "checkNo",
      header: "Check",
      type: "custom",
      render: (_: any, row: any) => (
        <div>
          <div className="font-bold text-gray-900">{row.checkNo}</div>
        </div>
      ),
    },
    {
      key: "consumerName",
      header: "Consumer",
      type: "custom",
      render: (_: any, row: any) => (
        <div>
          <div className="font-bold text-gray-900">{row.consumerName}</div>
          <div className="text-gray-400 text-xs">{row.consumerAcc}</div>
        </div>
      ),
    },
    {
      key: "bankName",
      header: "Bank / Branch",
      type: "custom",
      render: (_: any, row: any) => (
        <div>
          <div className="text-gray-900">{row.bankName}</div>
          <div className="text-gray-400 text-xs">{row.branchName}</div>
        </div>
      ),
    },
    {
      key: "amount",
      header: `Amount (${currency})`,
      type: "custom",
      render: (value: any) => (
        <span className="font-bold text-gray-900">{value}</span>
      ),
    },
    {
      key: "penalty",
      header: `Penalty (${currency})`,
      type: "custom",
      render: (value: any) => (
        <span
          className={value !== "—" ? "text-red-500 font-bold" : "text-gray-400"}
        >
          {value}
        </span>
      ),
    },
    {
      key: "checkDate",
      header: "Check Date",
      type: "custom",
      render: (value: any) => <span className="text-gray-700">{value}</span>,
    },
    {
      key: "depositBy",
      header: "Deposited Due",
      type: "custom",
      render: (value: any, row: any) => {
        if (row.depositStatus === "warning") {
          return (
            <Badge
              variant="outline"
              className="bg-amber-100 text-amber-800 border-amber-200"
            >
              {value}
            </Badge>
          );
        }
        if (row.depositStatus === "destructive") {
          return (
            <Badge
              variant="destructive"
              className="bg-red-100 text-red-800 hover:bg-red-200 border-red-200"
            >
              {value}
            </Badge>
          );
        }
        return <span className="text-gray-700">{value}</span>;
      },
    },
    {
      key: "source",
      header: "Channel",
      type: "custom",
      render: (value: any) => {
        let badgeColor = "bg-blue-50 text-blue-600 border-blue-200";
        if (value.includes("Field")) {
          badgeColor = "bg-purple-50 text-purple-600 border-purple-200";
        }
        return (
          <Badge variant="outline" className={badgeColor}>
            {value}
          </Badge>
        );
      },
    },
    {
      key: "status",
      header: "Status",
      type: "custom",
      render: (value: any) => {
        let badgeColor = "bg-amber-100 text-amber-700 border-amber-200";
        if (value === "Cleared")
          badgeColor = "bg-green-100 text-green-700 border-green-200";
        if (value === "Dishonoured")
          badgeColor = "bg-red-100 text-red-700 border-red-200";
        return (
          <Badge variant="outline" className={badgeColor}>
            {value}
          </Badge>
        );
      },
    },
    {
      key: "hold",
      header: "Hold",
      type: "custom",
      render: (value: any) => {
        if (value === "Active") {
          return (
            <Badge
              variant="outline"
              className="bg-green-50 text-green-700 border-green-200 gap-1 pr-2"
            >
              <Lock className="w-3 h-3" />
              {value}
            </Badge>
          );
        }
        return (
          <Badge
            variant="outline"
            className="bg-gray-50 text-gray-400 border-gray-200"
          >
            {value}
          </Badge>
        );
      },
    },
  ];

  const filterPanel = (
    <div className="space-y-4">
      <CheckRegisterFilter
        currentFilters={currentFilters}
        onApplyFilters={handleApplyFilters}
        onResetFilters={handleResetFilters}
      />
    </div>
  );

  const [selectedCheck, setSelectedCheck] = useState<any>(null);

  const actions: TableAction<any>[] = [
    {
      label: "View Details",
      onClick: (row: any) => setSelectedCheck(row),
    },
  ];

  const handleUpdateStatus = (id: string, newStatus: string) => {
    if (selectedCheck && selectedCheck.id === id) {
      setSelectedCheck({ ...selectedCheck, status: newStatus });
    }
  };

  const tabProps = {
    currentSearch,
    currentBank,
    currentSource,
    currentPage,
    columns,
    actions,
    filterPanel,
    handleSearch,
    handlePageChange,
  };

  const tabComponents = {
    all: {
      label: "All",
      component: <TableContent statusTab="" {...tabProps} />,
    },
    pending: {
      label: "Pending",
      component: <TableContent statusTab="pending" {...tabProps} />,
    },
    cleared: {
      label: "Cleared",
      component: <TableContent statusTab="cleared" {...tabProps} />,
    },
    dishonoured: {
      label: "Dishonoured",
      component: <TableContent statusTab="dishonoured" {...tabProps} />,
    },
  };

  return (
    <div className="mt-6">
      <Tabs
        defaultValue="all"
        className="w-full"
        tabsListClassName="grid grid-cols-4"
        tabComponents={tabComponents}
        disableUrlSync={true}
      />
      <CheckDetailModal
        isOpen={!!selectedCheck}
        onClose={() => setSelectedCheck(null)}
        data={selectedCheck}
        onUpdateStatus={handleUpdateStatus}
      />
    </div>
  );
};
